import Component from "../../lib/component";
import {identifyElement} from "../../helpers/element";
import {JSVoidString} from "../../helpers/js_void";
import {DropdownNav} from "./dropdown_nav";
import {DropdownBtn} from "./dropdown_btn";

export const DropdownConfig = {
    componentTypeKey: 'Dropdown',
    baseHtmlClass: 'dropdown',
    selector: '.dropdown',

    datasetConfigKey: 'dropdown__config',

    // Fired on the dropdown's own element when a SELECT item (one carrying a `value`) is
    // activated. Consumers subscribe via `element.addEventListener(Dropdown.config.selectedEventName, …)`
    // — event name in Config, never a literal (matches CollapsableFieldset / RecordNester / Selecter).
    selectedEventName: 'Rankers.Dropdown:Selected',
};

export class Dropdown extends Component {

    static get config() {
        return DropdownConfig;
    }

    /**
     * Build the DOM for a Dropdown — a trigger element plus the dropdown
     * shell. Callers append each returned element where they want in the DOM;
     * the framework's component initializer picks the dropdown up via its
     * MutationObserver and instantiates this class automatically.
     *
     * @param {Object} config
     * @param {Object} config.trigger - Trigger element config.
     * @param {string} [config.trigger.tagName='button'] - HTML tag for the trigger.
     * @param {string} config.trigger.label - Text content of the trigger.
     * @param {string} [config.trigger.className] - Additional class(es) for the trigger.
     * @param {boolean} [config.showChevron=false] - Append a down-chevron SVG to the trigger as an "I open a menu" affordance.
     * @param {string} [config.title] - Optional title row text. Rendered as `<li class="dropdown_title"><span>{title}</span></li>`.
     * @param {Array<{label: string, anchor?: string, value?: string}>} [config.items] - Static items. Mutually exclusive with `inPageScan`.
     *   Each item is one of two kinds, discriminated by which key it carries:
     *   a LINK item (`{label, anchor}`) renders `<a href>` and navigates (unchanged behaviour);
     *   a SELECT item (`{label, value}`) renders `<button data-value>` and, on activation, fires
     *   `DropdownConfig.selectedEventName` on the dropdown element with `{value, label}` in `detail`.
     *   The two kinds are mixable in one menu.
     * @param {string} [config.selected] - For a select menu: the `value` whose label seeds the trigger when `trigger.label` is not given.
     * @param {boolean} [config.updateTriggerLabelOnSelect=false] - When true, activating a select item updates the trigger label to that item's label.
     * @param {Object} [config.inPageScan] - In-page-nav config. The dropdown scans the page for sections and renders them dynamically.
     * @param {string} config.inPageScan.sectionSelector - CSS selector for each section element on the page.
     * @param {string} config.inPageScan.titleSelector - CSS selector (relative to each section) for the section's heading element.
     * @param {string} [config.inPageScan.groupSelector] - Optional CSS selector for a group ancestor; enables grouped rendering.
     * @param {string} [config.inPageScan.groupTitleSelector] - CSS selector (relative to each group) for the group's heading element. Required if `groupSelector` is set.
     * @returns {{trigger: HTMLElement, dropdown: HTMLElement}}
     */
    static build(config = {}) {
        const triggerConfig = {...(config.trigger || {})};

        // Seed the trigger label from the initially-selected item's label when the caller
        // hasn't given an explicit trigger label (select menus). Label-only: does NOT act on
        // the map/consumer — the consumer owns any initial side effect.
        if (!triggerConfig.label && config.selected != null && Array.isArray(config.items)) {
            const selectedItem = config.items.find((item) => item.value === config.selected);
            if (selectedItem) triggerConfig.label = selectedItem.label;
        }

        const trigger = buildTriggerElement(triggerConfig);

        const dropdown = buildDropdownElement(trigger.id, config);

        return {trigger, dropdown};
    }

    constructor(id_or_element) {
        super({
            element: id_or_element,
            config: Dropdown.config
        });

        this.setDatasetConfig(Dropdown.config.datasetConfigKey);

        this.dropdownBtn = this.getDropdownBtn();
        this.dropdownNav = getDropdownNav(this);

        document.addEventListener('click', this.handleDocumentClick.bind(this));

        this.open = false;
        this.element.style.display = null;
        this.render();
    }

    render() {
        this.swapHtmlClassStates(this.isOpen(), 'open', 'closed');
        this.dropdownBtn.render();
        this.dropdownNav.render();
    }

    isOpen() {
        return this.open;
    }

    toggle() {
        this.open = !this.isOpen();
    }

    close() {
        this.open = false;
    }

    handleDocumentClick(event) {
        if (!this.isOpen()) return;
        if (this.dropdownBtn.element.contains(event.target)) return;
        if (this.dropdownNav.element.contains(event.target)) return;
        event.preventDefault();
        this.close();
        this.render();
    }

    // Update only the trigger's label span, leaving the chevron span (appended by DropdownBtn)
    // untouched. Called by DropdownNav when a select item is activated with updateTriggerLabelOnSelect.
    setTriggerLabel(text) {
        const labelElement = this.dropdownBtn.element.querySelector('.dropdown_btn__label');
        if (labelElement) {
            labelElement.textContent = text;
        } else {
            console.warn('[Dropdown] setTriggerLabel: no .dropdown_btn__label span in trigger; label not updated.', this);
        }
    }

    getDropdownBtn() {
        let element = null;
        const id = this.getDatasetConfigValue('btn_id');
        if (id) element = document.getElementById(id);
        if (!element) console.warn(`[Dropdown] No btn element found with ID of ${id}:`, this);
        const options = { show_chevron: this.getDatasetConfigValue('show_chevron') };
        return new DropdownBtn(element, this, options);
    }

}

function getDropdownNav(dropdown) {
    const element = dropdown.element.querySelector(DropdownNav.config.selector);
    return new DropdownNav(element, dropdown);
}

function buildTriggerElement(triggerConfig) {
    const tagName = triggerConfig.tagName || 'button';
    const element = document.createElement(tagName);
    if (triggerConfig.className) {
        triggerConfig.className.split(/\s+/).filter(Boolean).forEach((c) => element.classList.add(c));
    }
    // Label lives in its own span so setTriggerLabel() can update it without disturbing the
    // chevron span DropdownBtn appends alongside it.
    if (triggerConfig.label) {
        const labelSpan = document.createElement('span');
        labelSpan.classList.add('dropdown_btn__label');
        labelSpan.textContent = triggerConfig.label;
        element.appendChild(labelSpan);
    }
    // `<a>` triggers need an href to be keyboard-focusable and to receive
    // hover/focus styles. The DropdownBtn click handler preventDefault()s the
    // navigation, so a no-op href is appropriate.
    if (tagName === 'a' && !element.hasAttribute('href')) element.setAttribute('href', JSVoidString);
    // Assign a stable id so the Dropdown can find this element via btn_id.
    identifyElement(element);
    return element;
}

function buildDropdownElement(triggerId, config) {
    const element = document.createElement('div');
    element.classList.add('dropdown');
    element.style.display = 'none'; // Dropdown constructor clears this once it inits.
    const dropdownConfig = {btn_id: triggerId};
    if (config.showChevron) dropdownConfig.show_chevron = true;
    element.setAttribute('data-dropdown__config', JSON.stringify(dropdownConfig));

    const nav = document.createElement('nav');
    nav.classList.add('dropdown_nav');

    const navConfig = buildNavConfig(config);
    if (navConfig) {
        nav.setAttribute('data-dropdown_nav__config', JSON.stringify(navConfig));
    }

    const list = document.createElement('ul');
    if (config.title) {
        const titleLi = document.createElement('li');
        titleLi.classList.add('dropdown_title');
        const titleSpan = document.createElement('span');
        titleSpan.textContent = config.title;
        titleLi.appendChild(titleSpan);
        list.appendChild(titleLi);
    }
    if (Array.isArray(config.items)) {
        config.items.forEach((item) => list.appendChild(buildStaticItemLi(item)));
    }
    nav.appendChild(list);
    element.appendChild(nav);

    return element;
}

function buildNavConfig(config) {
    const navConfig = {};
    if (config.inPageScan) {
        const scan = config.inPageScan;
        const inPageConfig = {
            section_selector: scan.sectionSelector,
            title_selector: scan.titleSelector,
        };
        if (scan.groupSelector) inPageConfig.group_selector = scan.groupSelector;
        if (scan.groupTitleSelector) inPageConfig.group_title_selector = scan.groupTitleSelector;
        navConfig.in_page_config = inPageConfig;
    }
    if (config.updateTriggerLabelOnSelect) navConfig.update_trigger_label_on_select = true;
    return (Object.keys(navConfig).length > 0) ? navConfig : null;
}

function buildStaticItemLi(item) {
    const li = document.createElement('li');
    li.classList.add('dropdown_nav__item');
    const option = document.createElement('div');
    option.classList.add('dropdown_option');
    option.appendChild(buildOptionControl(item));
    li.appendChild(option);
    return li;
}

// Build the interactive control for one item. A SELECT item (carries `value`) is an action →
// `<button data-value>`; a LINK item (carries `anchor`) is navigation → `<a href>`. Shared by
// the static-items path (here) and the in-page-scan path (DropdownNav.createItemLi), which imports
// this so the two can't drift.
export function buildOptionControl(item) {
    const isSelect = (item.value !== undefined && item.value !== null);
    if (isSelect && item.anchor) {
        // An item shouldn't be both a link and an action; prefer select and flag the ambiguity.
        console.warn('[Dropdown] item carries both `value` and `anchor`; treating it as a select item:', item);
    }
    if (isSelect) {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.value = item.value;
        button.textContent = item.label;
        return button;
    }
    const link = document.createElement('a');
    link.href = item.anchor;
    link.textContent = item.label;
    return link;
}
